// @ts-nocheck
import {
    sideMenuLinkActiveStyles,
    sideMenuLinkStyles,
    sideMenuWrapperStyles,
} from "@store/Themes/common/mixins/sidemenu";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";

import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import overrideProps from "@toolkit/Framework/Common/Utilities/overrideProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

export type SideMenuLinkPresentationProps = LinkPresentationProps & {
    isCurrent?: boolean;
};

export interface SideMenuStyles {
    wrapper?: InjectableCss;
    list?: InjectableCss;
    listItem?: InjectableCss;
    link?: SideMenuLinkPresentationProps;
    signOutLink?: SideMenuLinkPresentationProps;
    // bunzl custom code
    activeStyles?: InjectableCss;
}

export const sideMenuBaselinkStyles = (linksTakeAllWidth = false) => css<SideMenuLinkPresentationProps>`
    ${sideMenuLinkStyles(undefined, linksTakeAllWidth)}

    ${({ isCurrent }) =>
        isCurrent &&
        css`
            ${sideMenuLinkActiveStyles()}
        `}
`;

export const sideMenuStyles = {
    wrapper: {
        css: sideMenuWrapperStyles(`${pxToRem(5)} 0`),
    },
    list: {},
    listItem: {
        css: contentSpacingStyles(0, 0, pxToRem(5)),
    },
    link: {
        css: sideMenuBaselinkStyles(true),
    },
    signOutLink: {
        css: css<SideMenuLinkPresentationProps>`
            ${sideMenuBaselinkStyles(true)}
            color: ${getColor("primary")};
        `,
    },
};

const List = styled.ul`
    ${injectCss}
`;
const ListItem = styled.li`
    ${injectCss}
`;
const StyledLink = styled(Link)<SideMenuLinkPresentationProps>``;

interface OwnProps {
    pageType?: string;
    nodeId?: string;
    showRootPage?: boolean;
    showSignOut?: boolean;
    overridedStyles?: SideMenuStyles;
    extendedStyles?: SideMenuStyles;
    onClick?: React.EventHandler<React.MouseEvent>;
    excludedPageTypes?: string[];
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    rootPageLink: props.pageType
        ? getPageLinkByPageType(state, props.pageType)
        : getPageLinkByNodeId(state, props.nodeId || getCurrentPage(state).nodeId),
    currentLocation: getLocation(state),
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    signOut,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SideMenu: React.FunctionComponent<Props> = ({
    showRootPage,
    showSignOut,
    overridedStyles,
    extendedStyles,
    onClick,
    rootPageLink,
    currentLocation,
    isSignedIn,
    signOut,
    excludedPageTypes = [],
}) => {
    const styles = useMergeProps(overrideProps(sideMenuStyles, overridedStyles), extendedStyles);
    if ((!rootPageLink || !rootPageLink.children) && !showSignOut) {
        return null;
    }
    const currentPageUrl = currentLocation.pathname.toLowerCase();

    const _excludedPageTypes = [...excludedPageTypes];

    const _links = rootPageLink?.children?.filter(
        x => !x.excludeFromNavigation && !_excludedPageTypes.some(y => x.type === y),
    );

    /* bunzl custom code start */
    const extendedActiveStyles = useMemo(
        () => ({
            ...styles.link,
            css: css`
                ${styles.link.css}
                ${styles.activeStyles?.css}
            `,
        }),
        [styles.link, styles.activeStyles],
    );
    const rootPageIsCurrent = currentPageUrl === rootPageLink?.url.toLowerCase();
    return (
        <StyledWrapper as="nav" {...styles.wrapper} className="sidemenu-wrapper">
            <List {...styles.list} className="sidemenu-list">
                {showRootPage && rootPageLink && (
                    <ListItem {...styles.listItem} className="sidemenu-listitem">
                        <StyledLink
                            href={rootPageLink.url}
                            isCurrent={rootPageIsCurrent}
                            onClick={onClick}
                            {...(rootPageIsCurrent ? extendedActiveStyles : styles.link)}
                            className={`sidemenu-link ${rootPageIsCurrent ? "active" : ""}`}
                        >
                            {rootPageLink.title}
                        </StyledLink>
                    </ListItem>
                )}

                {_links?.map(link => {
                    const isCurrent = currentPageUrl.startsWith(link.url.toLowerCase());
                    const linkStyles = isCurrent ? extendedActiveStyles : styles.link;
                    return (
                        <ListItem key={link.id} {...styles.listItem} className="sidemenu-listitem">
                            <StyledLink
                                href={link.url}
                                isCurrent={isCurrent}
                                className={`sidemenu-link ${isCurrent ? "active" : ""}`}
                                onClick={onClick}
                                {...linkStyles}
                            >
                                {link.title}
                            </StyledLink>
                        </ListItem>
                    );
                })}

                {isSignedIn && showSignOut && (
                    <ListItem {...styles.listItem} className="sidemenu-listitem">
                        <StyledLink
                            className="sidemenu-link"
                            {...styles.link}
                            onClick={(event: React.MouseEvent) => {
                                signOut();
                                onClick?.(event);
                            }}
                        >
                            {translate("Sign Out")}
                        </StyledLink>
                    </ListItem>
                )}
            </List>
            {/* bunzl custom code end */}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SideMenu);
